import { Archive, ArchiveRestore, X } from 'lucide-react';
import { Badge, Button, cn, EmptyState } from '@eo/shared';

import { excessGroups, formatBillHours } from './helpers';
import { HoursStepper } from './HoursStepper';
import type { ExcessEntriesProps } from './types';

const dateLabel = (ts: number) =>
  new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

export function ExcessEntries({
  lines,
  categoryOrder,
  onHours,
  onToggleArchive,
}: ExcessEntriesProps) {
  if (lines.length === 0) {
    return <EmptyState title="No variable hours were logged for this client this month." />;
  }

  return (
    <table className="w-full text-sm" data-testid="excess-entries">
      <thead>
        <tr className="border-b border-text-primary text-left text-xs text-text-tertiary">
          <th className="py-2 pr-4 font-normal">Date</th>
          <th className="py-2 pr-4 font-normal">Person</th>
          <th className="py-2 pr-4 font-normal">Task</th>
          <th className="w-28 py-2 pr-2 text-right font-normal">Hours</th>
          <th className="w-10 py-2" aria-label="Actions" />
        </tr>
      </thead>
      {excessGroups(lines, categoryOrder).map((group) => (
        <tbody key={group.category} data-testid="excess-category">
          <tr className="border-b border-border-faint">
            <th
              scope="rowgroup"
              colSpan={3}
              className="pt-5 pb-2 text-left font-normal text-text-primary"
            >
              {group.category}
            </th>
            <td className="pt-5 pb-2 pr-2 text-right tabular-nums text-text-primary">
              {formatBillHours(group.hours)}
            </td>
            <td />
          </tr>
          {group.lines.map((line) => (
            <tr
              key={line.key}
              data-testid="excess-entry"
              className={cn(
                'border-b border-border-faint text-text-secondary',
                line.archived && 'text-text-tertiary',
              )}
            >
              <td className="py-1.5 pr-4 tabular-nums">{dateLabel(line.startTs)}</td>
              <td className="py-1.5 pr-4">{line.member}</td>
              <td className="py-1.5 pr-4">
                <span
                  className={cn(
                    'inline-flex flex-wrap items-center gap-2',
                    line.archived && 'line-through',
                  )}
                >
                  {line.taskName}
                  {line.added && <Badge variant="secondary">New</Badge>}
                  {line.edited && <Badge variant="secondary">Edited</Badge>}
                  {line.archived && <Badge variant="secondary">Archived</Badge>}
                </span>
              </td>
              <td className="py-1.5 pr-2">
                <HoursStepper
                  label={`Hours for ${line.taskName}`}
                  value={line.hours}
                  min={0.25}
                  disabled={line.archived}
                  onCommit={(hours) => onHours(line.key, hours)}
                />
              </td>
              <td className="py-1.5 text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={
                    line.added
                      ? `Remove ${line.taskName}`
                      : line.archived
                        ? `Restore ${line.taskName}`
                        : `Archive ${line.taskName}`
                  }
                  title={
                    line.added
                      ? 'Remove this new entry'
                      : line.archived
                        ? 'Put this entry back on the bill'
                        : 'Leave this entry off the bill. It stays in ClickUp.'
                  }
                  onClick={() => onToggleArchive(line.key)}
                >
                  {line.added ? <X /> : line.archived ? <ArchiveRestore /> : <Archive />}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      ))}
    </table>
  );
}
